import { create } from 'zustand';

import {
  type ConflictenFilter,
  defaultFilter as defaultConflictenFilter,
} from '@/components/sidebar/conflicten/conflictenFilter/conflictenFilterModel';
import {
  defaultFilter as defaultDefectenFilter,
  type DefectenFilter,
} from '@/components/sidebar/defecten/defectenFilter/defectenFilterModel';
import {
  defaultFilter as defaultSignalenFilter,
  type SignalenFilter,
} from '@/components/sidebar/signalen/signalenFilter/signalenFilterModel';
import type { Conflict } from '@/types/Conflicten';
import type { Defect } from '@/types/Defecten';
import type { Signaal } from '@/types/Signalen';

export type SidebarStore = {
  activePanel: 'Conflicten' | 'Defecten' | 'Signalen' | 'Mwd' | undefined;
  signalen: Signaal[];
  conflicten: Conflict[];
  conflictenRefreshVersion: number;
  conflictenFilter: ConflictenFilter;
  defecten: Defect[];
  defectenRefreshVersion: number;
  defectenFilter: DefectenFilter;
  signalenFilter: SignalenFilter;
  setActivePanel: (activePanel: SidebarStore['activePanel']) => void;
  setData: <K extends 'signalen' | 'conflicten' | 'defecten'>(key: K, value: SidebarStore[K]) => void;
  setConflictenFilter: (filter: ConflictenFilter) => void;
  setDefectenFilter: (filter: DefectenFilter) => void;
  setSignalenFilter: (filter: SignalenFilter) => void;
  triggerConflictenRefresh: () => void;
  triggerDefectenRefresh: () => void;
  reset: () => void;
};

const useSidebarStore = create<SidebarStore>((set) => ({
  activePanel: undefined,
  signalen: [],
  conflicten: [],
  conflictenRefreshVersion: 0,
  conflictenFilter: defaultConflictenFilter,
  defecten: [],
  defectenRefreshVersion: 0,
  defectenFilter: defaultDefectenFilter,
  signalenFilter: defaultSignalenFilter,
  setActivePanel: (activePanel) => set({ activePanel }),
  setData: (key, value) => {
    set((state) => ({
      ...state,
      [key]: value,
    }));
  },
  setConflictenFilter: (filter) => set({ conflictenFilter: filter }),
  setDefectenFilter: (filter) => set({ defectenFilter: filter }),
  setSignalenFilter: (filter) => set({ signalenFilter: filter }),
  triggerConflictenRefresh: () =>
    set((state) => ({
      conflictenRefreshVersion: state.conflictenRefreshVersion + 1,
    })),
  triggerDefectenRefresh: () =>
    set((state) => ({
      defectenRefreshVersion: state.defectenRefreshVersion + 1,
    })),
  reset: () =>
    set(() => ({
      activePanel: undefined,
      signalen: [],
      conflicten: [],
      conflictenRefreshVersion: 0,
      conflictenFilter: defaultConflictenFilter,
      defecten: [],
      defectenRefreshVersion: 0,
      defectenFilter: defaultDefectenFilter,
      signalenFilter: defaultSignalenFilter,
    })),
}));

export default useSidebarStore;
